// Transient messages — the panel's way of answering something that just
// happened, where an inline notice has nowhere to live. Field problems still
// belong under the field; this is for the rest.
//
// Ported from the dietitian dashboard's `Toast`, down to the typed variants,
// the coloured left edge and the slide-out, so a message means the same thing
// and looks the same in both consoles. The queue lives in one provider above
// the router rather than in each page's own state, so a message raised as a
// screen navigates away is still standing on the screen that follows.
//
// What this replaced on the dashboard was a red bar pushed in above the
// figures: it moved the whole page down as it appeared, sat there until the
// screen was left, and said its piece in the one place a reader coming back
// to a working screen does not need it. A failure that a retry fixes is an
// event, not a state — and the state it *is* belongs where the missing figures
// are, as a dash and an empty table that says why.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from "lucide-react";

export type ToastType = "success" | "error" | "info" | "warning";

export interface ToastMessage {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

/** How long a message stands before it starts leaving. */
const DEFAULT_DURATION = 3000;

/** Must match the transition on the card, or it is removed mid-slide. */
const EXIT_MS = 300;

type Raise = (title: string, message?: string, duration?: number) => void;

interface ToastApi {
  success: Raise;
  error: Raise;
  warning: Raise;
  info: Raise;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Raises a transient message: `toast.success("Client added", "…")`. Throws
 * outside a `ToastProvider` rather than quietly doing nothing — a message that
 * never appears is a bug that would otherwise only surface in front of a user.
 */
export const useToast = (): ToastApi => {
  const toast = useContext(ToastContext);
  if (!toast) throw new Error("useToast must be called inside a <ToastProvider>");
  return toast;
};

/**
 * Left edge, icon and its halo, per variant. The colour is scoped to the left
 * side rather than set on all four: the other three carry the hairline below,
 * and a whole-border colour would fight it depending on which utility Tailwind
 * happened to emit last.
 */
const VARIANTS: Record<
  ToastType,
  { border: string; icon: string; iconBg: string; Icon: typeof Info }
> = {
  // Success wears the brand teal rather than a green: it is the app's colour
  // for "this worked", and the panel has no second positive shade.
  success: {
    border: "border-l-brand",
    icon: "text-brand",
    iconBg: "bg-brand-soft",
    Icon: CheckCircle,
  },
  error: {
    border: "border-l-[#FF3B30]",
    icon: "text-[#FF3B30]",
    iconBg: "bg-[#FFF5F5]",
    Icon: AlertCircle,
  },
  warning: {
    border: "border-l-[#FF9500]",
    icon: "text-[#FF9500]",
    iconBg: "bg-[#FFF4E5]",
    Icon: AlertTriangle,
  },
  info: {
    border: "border-l-[#007AFF]",
    icon: "text-[#007AFF]",
    iconBg: "bg-[#E5F2FF]",
    Icon: Info,
  },
};

const ToastCard: React.FC<{
  toast: ToastMessage;
  onClose: (id: string) => void;
}> = ({ toast, onClose }) => {
  const [exiting, setExiting] = useState(false);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const variant = VARIANTS[toast.type];

  // Leaving is two steps: the card slides out, and only then is it dropped
  // from the list — removing it on the spot would cut the animation.
  const close = useCallback(() => {
    setExiting(true);
    exitTimer.current = setTimeout(() => onClose(toast.id), EXIT_MS);
  }, [onClose, toast.id]);

  useEffect(() => {
    const timer = setTimeout(close, toast.duration ?? DEFAULT_DURATION);
    return () => {
      clearTimeout(timer);
      // A card dismissed by hand and then unmounted must not leave its exit
      // timer running to set state on something that is gone.
      if (exitTimer.current) clearTimeout(exitTimer.current);
    };
  }, [close, toast.duration]);

  return (
    <div
      // A full-weight hairline closes the top, right and bottom, so the card
      // reads as a sheet rather than three soft edges hanging off one coloured
      // one. The app's `line` at 1px rather than a half-pixel custom grey: at
      // 0.5px the browser rounds it away on ordinary displays, which left the
      // card looking like an accent bar with text floating beside it. The left
      // is re-stated after it, which is what lets the accent win: a per-side
      // utility overrides the all-sides one it follows.
      //
      // The shadow is deliberately heavier than anything else in the panel.
      // Every screen this lands on is white, so a card of the same white has
      // only its shadow to sit on: three layers — a tight contact edge, a
      // mid lift, and a wide soft cast — because one blur dark enough to be
      // seen at this size reads as a grey smudge instead of depth.
      className={`w-full rounded-lg border border-line border-l-4 bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.14),0_6px_14px_-3px_rgba(16,24,40,0.22),0_22px_48px_-12px_rgba(16,24,40,0.34)] transition-all duration-300 ${
        variant.border
      } ${
        exiting
          ? "translate-x-full opacity-0"
          : "translate-x-0 opacity-100 animate-toast-in"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 rounded-full p-1.5 ${variant.iconBg}`}>
          <variant.Icon aria-hidden="true" className={`h-5 w-5 ${variant.icon}`} />
        </div>

        <div className="min-w-0 flex-1">
          <h4 className="mb-0.5 text-sm font-semibold text-ink">{toast.title}</h4>
          {toast.message && (
            <p className="text-sm leading-snug text-ink-muted">{toast.message}</p>
          )}
        </div>

        <button
          type="button"
          onClick={close}
          aria-label="Dismiss message"
          className="flex-shrink-0 text-ink-subtle transition-colors hover:text-ink"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const raise = useCallback(
    (type: ToastType, title: string, message?: string, duration?: number) => {
      // The id is taken outside the updater: the updater has to stay pure, and
      // React may run it more than once for a single call.
      const id = `toast-${nextId.current++}`;

      // Saying the same thing twice stacks nothing — the standing copy is
      // replaced, which is what restarts its clock on a repeated click.
      setToasts((current) => [
        ...current.filter(
          (toast) => toast.title !== title || toast.message !== message
        ),
        { id, type, title, message, duration },
      ]);
    },
    []
  );

  // Rebuilt only when `raise` is, which is never — so a consumer reading the
  // api out of context is not re-rendered by every message that goes past.
  const api = useRef<ToastApi>({
    success: (title, message, duration) =>
      raise("success", title, message, duration),
    error: (title, message, duration) => raise("error", title, message, duration),
    warning: (title, message, duration) =>
      raise("warning", title, message, duration),
    info: (title, message, duration) => raise("info", title, message, duration),
  }).current;

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        // The live region is always mounted, empty or not: one announced late
        // is one a screen reader never reads. `pointer-events-none` on the
        // column keeps the space around the cards clickable, and the width is
        // capped against the viewport so a message never runs off a phone.
        //
        // Nothing clips this column. It is the exact width of the cards, so an
        // `overflow-hidden` here cuts every shadow off flush at the card's own
        // edge — the depth is drawn and then trimmed away, which is why a white
        // card on a white page looked flat. A card sliding out passes beyond
        // the viewport instead, and a fixed box does not extend the document's
        // scrollable area, so it costs no stray horizontal scrollbar.
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed right-4 top-4 z-[9999] flex w-[min(calc(100vw-2rem),24rem)] flex-col items-end gap-3"
        >
          {toasts.map((toast) => (
            <div key={toast.id} className="pointer-events-auto w-full">
              <ToastCard toast={toast} onClose={dismiss} />
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

export default ToastProvider;
